import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Space, Spin, Empty, Tabs, Select, Modal, Typography } from 'antd';
import { CheckOutlined, DeleteOutlined, BellOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { notificationsAPI } from '../../../../../services/notifications/notifications.api';
import { TNotification, NotificationType } from '../../../../../types/notification';
import { useAuth } from '../../../../../store';
import { useNavigate } from 'react-router-dom';
import { formatMentions } from '../../../../../utils/formatMentions';

// Enable relative time plugin for dayjs
dayjs.extend(relativeTime);

const { TabPane } = Tabs;
const { Option } = Select;
const { Text } = Typography;

export const UserNotificationsTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<TNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const [filterType, setFilterType] = useState<NotificationType | undefined>();
  const [selectedNotification, setSelectedNotification] = useState<TNotification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [user?.id, activeTab, filterType]);

  const fetchNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const response = await notificationsAPI.getNotifications({
        userId: user.id,
        page: 1,
        pageSize: 50,
        isRead: activeTab === 'unread' ? false : undefined,
        notificationType: filterType,
      });
      setNotifications(response.items);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    if (!user?.id) return;

    try {
      const count = await notificationsAPI.getUnreadCount(user.id);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;

    try {
      await notificationsAPI.markAllAsRead(user.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (notificationId: string) => {
    try {
      await notificationsAPI.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const getTypeColor = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.MENTION:
        return 'blue';
      case NotificationType.JOB_ASSIGNMENT:
        return 'orange';
      case NotificationType.SERVICE_TICKET_ASSIGNMENT:
        return 'purple';
      case NotificationType.TASK_ASSIGNMENT:
        return 'green';
      case NotificationType.TASK_UPDATE:
        return 'cyan';
      case NotificationType.JOB_UPDATE:
        return 'magenta';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.MENTION:
        return 'Mention';
      case NotificationType.JOB_ASSIGNMENT:
        return 'Job Assignment';
      case NotificationType.SERVICE_TICKET_ASSIGNMENT:
        return 'Service Assignment';
      case NotificationType.TASK_ASSIGNMENT:
        return 'Task Assignment';
      case NotificationType.TASK_UPDATE:
        return 'Task Update';
      case NotificationType.JOB_UPDATE:
        return 'Job Update';
      default:
        return 'Notification';
    }
  };

  const handleNotificationClick = async (notification: TNotification) => {
    // Mark as read when clicked
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type and metadata
    if (notification.metadata?.jobTicketId) {
      navigate(`/bookings/${notification.metadata.jobTicketId}`);
    } else if (notification.serviceTicketId) {
      // Could navigate to service ticket if there's a dedicated view
      setSelectedNotification(notification);
      setShowDetailModal(true);
    } else if (notification.taskId) {
      // Could navigate to task if there's a dedicated view
      setSelectedNotification(notification);
      setShowDetailModal(true);
    } else {
      // Default: show detail modal
      setSelectedNotification(notification);
      setShowDetailModal(true);
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'isRead',
      key: 'isRead',
      width: 80,
      render: (isRead: boolean) => (
        <div style={{ textAlign: 'center' }}>
          {!isRead && (
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#1890ff',
                margin: '0 auto',
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'notificationType',
      key: 'notificationType',
      width: 150,
      render: (type: NotificationType) => (
        <Tag color={getTypeColor(type)}>{getTypeLabel(type)}</Tag>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: TNotification) => (
        <div>
          <div style={{ fontWeight: record.isRead ? 'normal' : 'bold' }}>
            {title}
          </div>
          {record.triggeredByUser && (
            <div style={{ fontSize: 12, color: '#666' }}>
              from {record.triggeredByUser.firstName}{' '}
              {record.triggeredByUser.lastName}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Message',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string) => formatMentions(content),
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: Date) => dayjs(date).fromNow(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: TNotification) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleNotificationClick(record);
            }}
          >
            View
          </Button>
          {!record.isRead && (
            <Button
              type="link"
              size="small"
              icon={<CheckOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAsRead(record.id);
              }}
            >
              Mark Read
            </Button>
          )}
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record.id);
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>
            <BellOutlined /> My Notifications
          </h2>
          {unreadCount > 0 && (
            <Tag color="blue">{unreadCount} unread</Tag>
          )}
        </div>
        <Space>
          <Select
            style={{ width: 200 }}
            placeholder="Filter by type"
            allowClear
            value={filterType}
            onChange={setFilterType}
          >
            <Option value={NotificationType.MENTION}>Mentions</Option>
            <Option value={NotificationType.JOB_ASSIGNMENT}>
              Job Assignments
            </Option>
            <Option value={NotificationType.SERVICE_TICKET_ASSIGNMENT}>
              Service Assignments
            </Option>
            <Option value={NotificationType.TASK_ASSIGNMENT}>
              Task Assignments
            </Option>
            <Option value={NotificationType.TASK_UPDATE}>Task Updates</Option>
            <Option value={NotificationType.JOB_UPDATE}>Job Updates</Option>
          </Select>
          <Button type="primary" onClick={handleMarkAllAsRead}>
            Mark All as Read
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as any)}>
        <TabPane tab="All Notifications" key="all" />
        <TabPane
          tab={`Unread (${unreadCount})`}
          key="unread"
        />
      </Tabs>

      <Spin spinning={loading}>
        {notifications.length === 0 ? (
          <Empty description="No notifications" />
        ) : (
          <Table
            columns={columns}
            dataSource={notifications}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} notifications`,
            }}
            rowClassName={(record) =>
              !record.isRead ? 'bg-blue-50' : ''
            }
            onRow={(record) => ({
              onClick: () => handleNotificationClick(record),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Spin>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <Tag color={getTypeColor(selectedNotification?.notificationType || NotificationType.GENERAL)}>
              {getTypeLabel(selectedNotification?.notificationType || NotificationType.GENERAL)}
            </Tag>
            <span>{selectedNotification?.title}</span>
          </Space>
        }
        open={showDetailModal}
        onCancel={() => {
          setShowDetailModal(false);
          setSelectedNotification(null);
        }}
        footer={[
          <Button key="close" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>,
          selectedNotification?.metadata?.jobTicketId && (
            <Button
              key="view-booking"
              type="primary"
              onClick={() => {
                navigate(`/bookings/${selectedNotification.metadata.jobTicketId}`);
                setShowDetailModal(false);
              }}
            >
              Go to Booking
            </Button>
          ),
        ].filter(Boolean)}
        width={700}
      >
        {selectedNotification && (
          <div>
            {/* Metadata Section */}
            {selectedNotification.metadata && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {selectedNotification.metadata.jobTicketRef && (
                    <div>
                      <Text strong>Job Ticket:</Text> {selectedNotification.metadata.jobTicketRef}
                    </div>
                  )}
                  {selectedNotification.metadata.serviceTicketRef && (
                    <div>
                      <Text strong>Service Ticket:</Text> {selectedNotification.metadata.serviceTicketRef}
                    </div>
                  )}
                  {selectedNotification.metadata.taskRef && (
                    <div>
                      <Text strong>Task:</Text> {selectedNotification.metadata.taskRef}
                    </div>
                  )}
                  {selectedNotification.metadata.clientName && (
                    <div>
                      <Text strong>Client:</Text> {selectedNotification.metadata.clientName}
                    </div>
                  )}
                  {selectedNotification.metadata.serviceType && (
                    <div>
                      <Text strong>Service Type:</Text> {selectedNotification.metadata.serviceType}
                    </div>
                  )}
                  {selectedNotification.metadata.taskTitle && (
                    <div>
                      <Text strong>Task Title:</Text> {selectedNotification.metadata.taskTitle}
                    </div>
                  )}
                </Space>
              </div>
            )}

            {/* Note Content */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Message:</Text>
              <div style={{
                padding: 12,
                background: '#fafafa',
                borderRadius: 4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {formatMentions(selectedNotification.content)}
              </div>
            </div>

            {/* Footer Info */}
            <div style={{ paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {selectedNotification.triggeredByUser && (
                  <Text type="secondary">
                    From: {selectedNotification.triggeredByUser.firstName}{' '}
                    {selectedNotification.triggeredByUser.lastName}
                  </Text>
                )}
                <Text type="secondary">
                  {dayjs(selectedNotification.createdAt).format('MMM D, YYYY [at] h:mm A')}
                  {' '}({dayjs(selectedNotification.createdAt).fromNow()})
                </Text>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
